import { useEffect, useMemo, useState } from 'react';

import type { LatestImage } from '@/@types/latestImages';

import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { getImageBrowserDisplayImages } from '../../multiFileViewer/findingViewer/imageBrowser/helpers';

import { ImageCarouselGrid } from './ImageCarouselGrid';
import { ImageFilterToggle, type ImageFilterType } from './ImageFilterToggle';
import { ImageNavigationControls } from './ImageNavigationControls';
import { useResponsiveCarousel } from './useResponsiveCarousel';

type CompareImageCarouselProps = {
  visitId?: number;
  onImageSelect?: (imageId: string) => void;
  selectedImageId?: string;
  section?: 'left' | 'right';
  filteredImages?: LatestImage[];
};

export const CompareImageCarousel = ({
  visitId,
  onImageSelect,
  selectedImageId,
  section,
  filteredImages,
}: CompareImageCarouselProps) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [imageFilter, setImageFilter] = useState<ImageFilterType[]>([
    'BW',
    'PA',
    'PHOTOS',
  ]);

  // Use responsive hook to get max images per page
  const maxImagesPerPage = useResponsiveCarousel();

  const { trackEvent } = useTrackEvent();

  // Filter images based on enabled types
  const images = useMemo(() => {
    // Use the filtered images passed from parent, or fallback to empty array
    const allImages: LatestImage[] = filteredImages || [];
    if (!allImages.length) return [];

    return allImages.filter((image) => {
      // If no filters are selected, show no images
      if (imageFilter.length === 0) return false;

      // Check if image matches any of the selected filters
      return imageFilter.some((filter) => {
        if (filter === 'BW') {
          return image.detectedImageType === 'BITEWING';
        } else if (filter === 'PA') {
          return image.detectedImageType === 'PERIAPICAL';
        } else if (filter === 'PHOTOS') {
          return image.detectedImageType === 'COLOR_IMAGE';
        }
        return false;
      });
    });
  }, [filteredImages, imageFilter]);

  // Count images for each filter type
  const imageCounts = useMemo(() => {
    const allImages: LatestImage[] =
      visitId && filteredImages ? filteredImages : [];

    const counts = {
      BW: 0,
      PA: 0,
      PHOTOS: 0,
    };

    allImages.forEach((image) => {
      if (image.detectedImageType === 'BITEWING') {
        counts.BW++;
      } else if (image.detectedImageType === 'PERIAPICAL') {
        counts.PA++;
      } else if (image.detectedImageType === 'COLOR_IMAGE') {
        counts.PHOTOS++;
      }
    });

    return counts;
  }, [visitId, filteredImages]);

  // Find and navigate to the page containing the active image
  useEffect(() => {
    if (selectedImageId && images) {
      const activeImageIndex = images.findIndex(
        (image) => image.imageId === selectedImageId,
      );
      if (activeImageIndex !== -1) {
        const pageNumber = Math.floor(activeImageIndex / maxImagesPerPage) + 1;
        setCurrentPage(pageNumber);
      }
    }
  }, [selectedImageId, images, maxImagesPerPage]);

  const handleMainFilterClick = (filters: string[]) => {
    const newFilters = filters as ImageFilterType[];
    setImageFilter(newFilters);

    trackEvent(mixpanelEvents.compareImageFilterChange, {
      section,
      visitId,
      filtersSelected: newFilters,
      totalFilters: newFilters.length,
      availableImageCounts: imageCounts,
    });
  };

  // Reset pagination when visitId or imageFilter changes
  useEffect(() => {
    // Reset to page 1 if:
    // 1. There's no selected image, OR
    // 2. The selected image doesn't exist in the current filtered images
    const hasSelectedImage =
      selectedImageId &&
      images?.some((image) => image.imageId === selectedImageId);

    if (!selectedImageId || !hasSelectedImage) {
      setCurrentPage(1);
    }
  }, [visitId, imageFilter, selectedImageId, images]);

  const displayImages =
    images && images.length > 0
      ? (getImageBrowserDisplayImages({
          numberOfImagesToDisplay: maxImagesPerPage,
          images: images as any, // Type cast to handle mixed LatestImage | ImageObject array
          page: currentPage,
        }) as LatestImage[])
      : [];

  const totalPages =
    images && images.length > 0
      ? Math.ceil(images.length / maxImagesPerPage)
      : 1;

  const currentImageIndex =
    images && selectedImageId
      ? images.findIndex((img) => img.imageId === selectedImageId) + 1
      : 0;

  const totalImages = images?.length || 0;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);

      // Track page change event
      trackEvent(mixpanelEvents.compareCarouselPageChange, {
        section,
        visitId,
        maxImagesPerPage,
        totalImages,
      });
    }
  };

  const handleImageNavigation = (direction: 'prev' | 'next') => {
    if (!images || !selectedImageId) return;

    const currentIndex = images.findIndex(
      (img) => img.imageId === selectedImageId,
    );
    if (currentIndex === -1) return;

    let newIndex;
    if (direction === 'prev' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    } else if (direction === 'next' && currentIndex < images.length - 1) {
      newIndex = currentIndex + 1;
    } else {
      return;
    }

    const newImageId = images[newIndex].imageId;
    if (onImageSelect) {
      onImageSelect(newImageId);
    }

    trackEvent(mixpanelEvents.compareImageNavigate, {
      section,
      visitId,
      direction,
      totalImages,
    });

    // Auto-navigate to the page containing the new image
    const pageNumber = Math.floor(newIndex / maxImagesPerPage) + 1;
    setCurrentPage(pageNumber);
  };

  const handleImageClick = (imageId: string) => {
    if (onImageSelect) {
      onImageSelect(imageId);

      const selectedImage = images?.find((img) => img.imageId === imageId);
      trackEvent(mixpanelEvents.compareImageSelect, {
        section,
        visitId,
        imageId,
        imageType: selectedImage?.detectedImageType,
      });
    }
  };

  return (
    <div className="vai-space-y-3 vai-w-full vai-max-w-full">
      <div className="vai-flex vai-items-center vai-gap-4">
        <ImageFilterToggle
          imageFilter={imageFilter}
          imageCounts={imageCounts}
          onFilterChange={handleMainFilterClick}
        />
        <ImageNavigationControls
          currentImageIndex={currentImageIndex}
          totalImages={totalImages}
          onNavigate={handleImageNavigation}
        />
      </div>

      <ImageCarouselGrid
        displayImages={displayImages}
        selectedImageId={selectedImageId}
        currentPage={currentPage}
        totalPages={totalPages}
        section={section}
        onImageClick={handleImageClick}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
